import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
   CButton, CCard, CCardBody, CCardHeader, CForm, CFormInput, CFormSelect, CCardFooter, CSpinner
} from '@coreui/react';

import { apiClient } from 'services/Api';
import { jwtDecode } from 'jwt-decode';
import { triggerAlert } from 'components/globals/AlertNotification';
import ToastNotification from 'components/globals/ToastNotification';
import TextEditor from 'components/forms/TextEditor';
import DateInput from 'components/forms/DateInput';
import UserSelectionModal from 'components/UserSelectionModal';

const Event = () => {
   const { id } = useParams();
   const navigate = useNavigate();
   const [formData, setFormData] = useState(null);
   const location = useLocation();
   const [users, setUsers] = useState([]);
   const [toast, setToast] = useState(null);
   const [showModal, setShowModal] = useState(false);
   const [isEditing, setIsEditing] = useState(true);
   const [loading, setLoading] = useState(false);
   const [participantsError, setParticipantsError] = useState('');
   const participantsSectionRef = useRef(null);

   const token = localStorage.getItem("access_token");
   const decodedToken = jwtDecode(token);
   const is_director = decodedToken.is_director;
   const user_id = decodedToken.user_id;

   const fetchEventDetails = async () => {
      let data = null; // Declare a variable to store API response

      try {
         const response = await apiClient.get(`/events/${id}/`);
         data = response.data; // Store response data
         setFormData(data); // Update state
      } catch (error) {
         triggerAlert('Formação/Evento não encontrada!', 'error');
         navigate("/formacoes-eventos");
      } finally {
         setIsEditing(data?.approved === null); // Use the local variable, NOT formData
      }
   };

   useEffect(() => {
      const fetchUsers = async () => {
         try {
            let response;
            if (formData !== null && formData.approved != null && formData.participants.length > 0) {
               response = await apiClient.get('/users/by-ids/', {
                  params: { ids: formData.participants },
               });
            } else {
               response = await apiClient.get('/users/active/');
            }
            setUsers(response.data);
         } catch (error) {
            console.error('Error fetching users:', error);
         }
      };

      if (!id) {
         setFormData({ proposed_by: user_id, participants: [] });
         setIsEditing(true);

      } else {
         fetchEventDetails();
      }
      fetchUsers();
   }, [id, location.pathname]);

   const handleInputChange = (e) => {
      const { name, value } = e.target;
      let updatedValue;

      if (value === "true") {
         updatedValue = true;
      } else if (value === "false") {
         updatedValue = false;
      } else {
         updatedValue = value || null; // Convert empty values to null
      }

      setFormData((prevFormData) => {
         let updatedForm = {
            ...prevFormData,
            [name]: updatedValue,
         };

         return updatedForm;
      });
   };

   const handleSubmit = async (e) => {
      e.preventDefault();

      if (!formData?.participants || formData.participants.length === 0) {
         setParticipantsError('Selecione pelo menos 1 participante.');

         // Scroll smoothly to the participants section
         if (participantsSectionRef.current) {
            participantsSectionRef.current.scrollIntoView({
               behavior: 'smooth',
               block: 'center',
            });
         }

         return; // prevent submission
      }


      setLoading(true); // Set loading state to true
      try {
         if (id) {
            await apiClient.put(`/events/${id}/`, formData);
            triggerAlert('Formação/Evento atualizada com sucesso!', 'success');
         } else {
            await apiClient.post('/events/', formData);
            triggerAlert('Formação/Evento criada com sucesso!', 'success');
         }
         navigate('/formacoes-eventos');
      } catch (error) {
         const errorMessage = error.response?.data?.error || 'Erro ao gravar';
         setToast({ message: { errorMessage }, type: 'error' });
         console.error('Error submitting form:', error);
         setLoading(false); // Reset loading state on error
      }
   };

   const handleCheckboxUsersChange = (userId, isChecked) => {
      setFormData((prevData) => {
         const updatedMembers = isChecked
            ? [...prevData.participants, userId]
            : prevData.participants.filter((id) => id !== userId);

         if (updatedMembers.length > 0) setParticipantsError('');

         return { ...prevData, participants: updatedMembers };
      });
   };

   if (formData !== null) {
      if (!isEditing && (formData.approved !== null || !is_director)) {
         return (
            <div className="container mt-4">
               <h4 className="text-center mb-5">Detalhes da Formação/Evento</h4>
               {[
                  { label: "Tipo", value: formData.event_type },
                  { label: "Designação", value: formData.designation || "-" },
                  { label: "Entidade prestadora", value: formData.providing_entity || "-" },
                  { label: "Local", value: formData.local || "-" },
                  { label: "Formador", value: formData.speaker || "-" },
                  { label: "Proposta por", value: formData.proposed_by_name || "-" },
                  {
                     label: "Participantes",
                     value: formData.participants.length > 0 ? (
                        <ul>
                           {users
                              .filter((user) => formData.participants.includes(user.id))
                              .map((user) => (
                                 <li key={user.id}>{`${user.first_name} ${user.last_name}`}</li>
                              ))}
                        </ul>
                     ) : (
                        <p className="text-muted">Nenhum participante selecionado</p>
                     )
                  },
                  { label: "Objetivos / Competências a adquirir", value: <div dangerouslySetInnerHTML={{ __html: formData.objectives || "-" }} /> },
                  { label: "Data de início", value: formData.start_date || "-" },
                  { label: "Data de fim", value: formData.end_date || "-" },
                  { label: "Nº de ações", value: formData.actions || "-" },
                  { label: "Horas totais", value: formData.hours || "-" },
                  { label: "Observações", value: <div dangerouslySetInnerHTML={{ __html: formData.obs || "-" }} /> },
                  { label: "Aprovado", value: formData.approved === true ? "Sim" : formData.approved !== false ? "Não" : "-" },
                  { label: "Data Aprovação", value: formData.approved_date || "-" }
               ].map((item, index) => (
                  <CCard key={index} className="mb-3">
                     <CCardHeader>
                        <h5>{item.label}</h5>
                     </CCardHeader>
                     <CCardBody>
                        <div>{item.value || ""}</div>
                     </CCardBody>
                  </CCard>
               ))}

               {/* Back Button */}
               <div className="d-flex justify-content-end gap-2 mt-3 mb-3">
                  <CButton color="danger" className="text-white" onClick={() => navigate('/formacoes-eventos')}>
                     Voltar
                  </CButton>
               </div>
            </div>
         );
      }

      return (
         <div className="container mt-4">
            {/* Error Toast - Appears on submit failure */}
            <ToastNotification message={toast?.message} type={toast?.type} visible={!!toast} />
            <CCard>
               <CCardHeader>
                  <h3>{id ? 'Editar Formação/Evento' : 'Nova Formação/Evento'}</h3>
               </CCardHeader>
               <CCardBody>
                  <CForm onSubmit={handleSubmit}>
                     <CFormSelect
                        label={<strong>Tipo</strong>}
                        name="event_type"
                        value={formData.event_type ?? ''}
                        onChange={handleInputChange}
                        className="mb-3"
                        required
                     >
                        <option value="">Selecione</option>
                        <option value="Formação">Formação</option>
                        <option value="Evento">Evento</option>
                        <option value="Académico">Académico</option>
                     </CFormSelect>

                     <CFormInput
                        type="text"
                        name="designation"
                        label={<strong>Designação</strong>}
                        value={formData.designation ?? ""}
                        required
                        onChange={handleInputChange}
                        className="mb-3"
                     />

                     <CFormInput
                        type="text"
                        name="providing_entity"
                        label={<strong>Entidade prestadora</strong>}
                        value={formData.providing_entity ?? ""}
                        onChange={handleInputChange}
                        className="mb-3"
                     />

                     <CFormInput
                        type="text"
                        name="event_place"
                        label={<strong>Local</strong>}
                        value={formData.event_place ?? ""}
                        onChange={handleInputChange}
                        className="mb-3"
                     />

                     <CFormInput
                        type="text"
                        name="speaker"
                        label={<strong>Formador</strong>}
                        value={formData.speaker ?? ""}
                        onChange={handleInputChange}
                        className="mb-3"
                     />

                     <CFormSelect
                        label={<strong>Proposta por</strong>}
                        name="proposed_by"
                        value={formData.proposed_by ?? user_id} // Default to logged-in user
                        onChange={handleInputChange}
                        className="mb-3"
                        required={true}
                     >
                        <option value="">Selecione um colaborador</option>
                        {users.map((user) => (
                           <option key={user.id} value={user.id}>
                              {user.first_name} {user.last_name}
                           </option>
                        ))}
                     </CFormSelect>

                     <CButton color="info" className="text-white" onClick={() => setShowModal(true)}>
                        Selecionar Participantes
                     </CButton>
                     <div className="mt-2" ref={participantsSectionRef}>
                        {formData.participants.length > 0 ? (
                           <ul>
                              {users
                                 .filter((user) => formData.participants.includes(user.id))
                                 .map((user) => (
                                    <li key={user.id}>{`${user.first_name} ${user.last_name}`}</li>
                                 ))}
                           </ul>
                        ) : (
                           <p className="text-muted">Nenhum participante selecionado</p>
                        )}
                        {participantsError && (
                           <small className="text-danger d-block mt-1">{participantsError}</small>
                        )}
                     </div>

                     <UserSelectionModal
                        title="Selecionar Participantes"
                        visible={showModal}
                        onClose={() => setShowModal(false)}
                        users={users}
                        selectedUsers={formData.participants}
                        onChange={handleCheckboxUsersChange}
                     />

                     <TextEditor
                        label="Objetivos / Competências a adquirir"
                        value={formData.objectives ?? ''}
                        onChange={(value) =>
                           handleInputChange({ target: { name: "objectives", value } })
                        }
                        className="mb-3"
                     />

                     <DateInput
                        label={<strong>Data de início</strong>}
                        name="start_date"
                        value={formData.start_date ?? ''}
                        onChange={handleInputChange}
                     />

                     <DateInput
                        label={<strong>Data de fim</strong>}
                        name="end_date"
                        value={formData.end_date ?? ''}
                        onChange={handleInputChange}
                     />

                     <CFormInput
                        type="number"
                        label={<strong>Nº de ações</strong>}
                        value={formData.actions || ""}
                        onChange={handleInputChange}
                        className="mb-3"
                        min="0"
                        name="actions"
                     />

                     <CFormInput
                        type="number"
                        label={<strong>Horas totais</strong>}
                        value={formData.hours || ""}
                        onChange={handleInputChange}
                        className="mb-3"
                        min="0"
                        step="0.01"
                        name="hours"
                     />

                     <TextEditor
                        label="Observações"
                        value={formData.obs ?? ''}
                        onChange={(value) =>
                           handleInputChange({ target: { name: "obs", value } })
                        }
                        className="mb-3"
                     />

                     {is_director && (
                        <>
                           <CFormSelect
                              label={<strong>Aprovado</strong>}
                              name="approved"
                              value={formData.approved ?? ''}
                              onChange={handleInputChange}
                              className="mb-3"
                           >
                              <option value="">Selecione</option>
                              <option value="true">Sim</option>
                              <option value="false">Não</option>
                           </CFormSelect>
                        </>
                     )}
                  </CForm>
               </CCardBody>
               <CCardFooter className="d-flex justify-content-end gap-2">
                  <CButton color="danger" className="text-white" onClick={() => navigate("/formacoes-eventos")}>
                     Cancelar
                  </CButton>
                  <CButton disabled={loading} color="success" className="text-white" type="submit" onClick={handleSubmit}>
                     {loading ? <CSpinner size="sm" /> : 'Confirmar'}
                  </CButton>
               </CCardFooter>
            </CCard>
         </div>
      );
   }
};

export default Event;